import {
	Apartment as ApartmentIcon,
	ArrowDropDown as ArrowDropDownIcon,
	ArrowDropUp as ArrowDropUpIcon,
	CheckBox as CheckBoxIcon,
	CheckBoxOutlineBlank as CheckBoxOutlineBlankIcon,
	DeveloperBoard as DeveloperBoardIcon,
	IndeterminateCheckBox as IndeterminateCheckBoxIcon
} from "@mui/icons-material";
import { Box, Button, ListItemIcon, Menu, MenuItem, Typography } from "@mui/material";
import { uniq } from "lodash";
import { FC, useEffect, useMemo, useRef, useState } from "react";

import { useAppSelector } from "../../redux";
import { useGetAllDevicesListQuery, useGetAllSocietiesListQuery } from "../../redux/reducers/settings.reducer";
import { IGetAllDevicesListResponseData, IGetAllSocietiesResponseData } from "../../types";
import { USER_ROLES_E } from "../../utils/constants";
import "./deviceSelection.css";

interface IDeviceSelectionProps {
	colorMode?: "light" | "dark";
	disableSelectAllSocieties?: boolean;
	disableSelectAllDevices?: boolean;
	multipleSocieties?: boolean;
	multipleDevices?: boolean;
	onChange: (deviceIds: number[], societyIds?: number[]) => void;
}

const DeviceSelection: FC<IDeviceSelectionProps> = (props) => {
	const {
		colorMode = "light",
		disableSelectAllSocieties,
		disableSelectAllDevices,
		multipleSocieties,
		multipleDevices,
		onChange
	} = props;

	const authState = useAppSelector((state) => state.auth);

	const societyDropdownAnchorRef = useRef<HTMLDivElement>(null);
	const deviceDropdownAnchorRef = useRef<HTMLDivElement>(null);

	const [societyDropdownOpen, setSocietyDropdownOpen] = useState<boolean>(false);
	const [deviceDropdownOpen, setDeviceDropdownOpen] = useState<boolean>(false);

	const [selectedSocieties, setSelectedSocieties] = useState<IGetAllSocietiesResponseData[]>([]);
	const [finalSelectedSocieties, setFinalSelectedSocieties] = useState<IGetAllSocietiesResponseData[]>([]);
	const [societyError, setSocietyError] = useState<string>("");

	const [selectedDevices, setSelectedDevices] = useState<IGetAllDevicesListResponseData[]>([]);
	const [finalSelectedDevices, setFinalSelectedDevices] = useState<IGetAllDevicesListResponseData[]>([]);
	const [deviceError, setDeviceError] = useState<string>("");

	// APIS
	// GET ALL SOCIETIES LIST
	const { data: getSocietysResponse } = useGetAllSocietiesListQuery();

	// GET ALL DEVICES LIST
	const { data: getDevicesListResponse } = useGetAllDevicesListQuery();

	const allSocietiesList = useMemo<IGetAllSocietiesResponseData[]>(() => {
		if (getSocietysResponse) return getSocietysResponse;
		return [];
	}, [getSocietysResponse]);

	const selectedSocietyIds = useMemo<number[]>(() => {
		return selectedSocieties.map((societyItem) => societyItem.id);
	}, [selectedSocieties]);

	const allDevicesList = useMemo<IGetAllDevicesListResponseData[]>(() => {
		if (!getDevicesListResponse) return [];

		if (finalSelectedSocieties.length > 0) {
			return getDevicesListResponse.filter((deviceItem) =>
				finalSelectedSocieties.some((societyItem) => societyItem.id === deviceItem.society_id)
			);
		}

		return getDevicesListResponse;
	}, [finalSelectedSocieties, getDevicesListResponse]);

	const selectedDeviceIds = useMemo<number[]>(() => {
		return selectedDevices.map((deviceItem) => deviceItem.id);
	}, [selectedDevices]);

	const societyDropdownText = useMemo(
		() => (multipleSocieties || finalSelectedSocieties.length === 0 ? "Property" : finalSelectedSocieties[0].name),
		[finalSelectedSocieties, multipleSocieties]
	);

	const deviceDropdownText = useMemo(() => {
		if (multipleDevices) {
			if (finalSelectedDevices.length === 1) return finalSelectedDevices[0].device_name;
			return "Devices";
		}

		if (finalSelectedDevices.length > 0) return finalSelectedDevices[0].device_name;
		if (disableSelectAllDevices) return "Device";
		return "All Devices";
	}, [disableSelectAllDevices, finalSelectedDevices, multipleDevices]);

	function handleChangeDevice(deviceIds: number[], societyIds?: number[]) {
		// if (
		// 	getDevicesListResponse &&
		// 	deviceIds.length === getDevicesListResponse.length &&
		// 	authState.user.role === USER_ROLES_E.SYSTEM_INTEGRATOR
		// ) {
		// 	onChange([]);
		// } else {
		onChange(deviceIds, societyIds);
		// }
	}

	function handleOpenSocietyDropdown() {
		if (allSocietiesList.length > 1) {
			setSelectedSocieties(finalSelectedSocieties);
			setSocietyDropdownOpen(true);
		}
	}

	function handleCloseSocietyDropdown(finalList?: IGetAllSocietiesResponseData[]) {
		setSocietyDropdownOpen(false);
		setSelectedSocieties([]);
		if (finalList) setFinalSelectedSocieties(finalList);
	}

	function handleSocietyItemClick(societyDetails?: IGetAllSocietiesResponseData) {
		if (multipleSocieties) {
			let updatedSocieties = [...selectedSocieties];

			if (societyDetails) {
				if (updatedSocieties.some((item) => item.id === societyDetails.id)) {
					// CLICKED SOCIETY IS ALREADY SELECTED
					updatedSocieties = updatedSocieties.filter((item) => item.id !== societyDetails.id);
				} else {
					// CLICKED SOCIETY IS NOT ALREADY SELECTED
					updatedSocieties.push(societyDetails);
				}
			} else {
				// SELECT ALL CLICKED
				if (updatedSocieties.length === allSocietiesList.length) updatedSocieties = [];
				else updatedSocieties = [...allSocietiesList];
			}

			setSelectedSocieties(updatedSocieties);
		} else {
			if (societyDetails) {
				handleCloseSocietyDropdown([societyDetails]);
			} else {
				handleCloseSocietyDropdown([]);
			}
		}
	}

	function handleSocietyApplyButtonClick() {
		if (selectedSocieties.length <= 0) {
			setSocietyError("At least one property should be selected");
		} else {
			handleCloseSocietyDropdown(selectedSocieties);
		}
	}

	function handleOpenDeviceDropdown() {
		if (allDevicesList.length > 1) {
			setSelectedDevices(finalSelectedDevices);
			setDeviceDropdownOpen(true);
		}
	}

	function handleCloseDeviceDropdown(finalList?: IGetAllDevicesListResponseData[]) {
		setDeviceDropdownOpen(false);
		setSelectedDevices([]);
		if (finalList) {
			setFinalSelectedDevices(finalList);
			const societyIds = finalList.map((item) => item.society_id);
			handleChangeDevice(
				finalList.map((item) => item.id),
				uniq(societyIds)
			);
		}
	}

	function handleDeviceItemClick(deviceDetails?: IGetAllDevicesListResponseData) {
		if (multipleDevices) {
			let updatedDevices = [...selectedDevices];

			if (deviceDetails) {
				if (updatedDevices.some((item) => item.id === deviceDetails.id)) {
					// CLICKED DEVICE IS ALREADY SELECTED
					updatedDevices = updatedDevices.filter((item) => item.id !== deviceDetails.id);
				} else {
					// CLICKED DEVICE IS NOT ALREADY SELECTED
					updatedDevices.push(deviceDetails);
				}
			} else {
				// SELECT ALL CLICKED
				if (updatedDevices.length === allDevicesList.length) updatedDevices = [];
				else updatedDevices = [...allDevicesList];
			}

			setSelectedDevices(updatedDevices);
		} else {
			if (deviceDetails) {
				handleCloseDeviceDropdown([deviceDetails]);
			} else {
				handleCloseDeviceDropdown([]);
			}
		}
	}

	function handleDeviceApplyButtonClick() {
		if (selectedDevices.length <= 0) {
			setSocietyError("At lease one device should be selected");
		} else {
			handleCloseDeviceDropdown(selectedDevices);
		}
	}

	function renderCheckbox(selectedCount: number, totalCount: number) {
		if (selectedCount === totalCount) return <CheckBoxIcon color="primary" />;
		if (selectedCount === 0) return <CheckBoxOutlineBlankIcon color="primary" />;
		return <IndeterminateCheckBoxIcon color="primary" />;
	}

	useEffect(() => {
		if (!societyDropdownOpen) {
			setSocietyError("");
		}
	}, [societyDropdownOpen]);

	useEffect(() => {
		if (!deviceDropdownOpen) {
			setDeviceError("");
		}
	}, [deviceDropdownOpen]);

	useEffect(() => {
		if (multipleSocieties) {
			setFinalSelectedSocieties(allSocietiesList);
		} else {
			setFinalSelectedSocieties(allSocietiesList.length > 0 && disableSelectAllSocieties ? [allSocietiesList[0]] : []);
		}
	}, [allSocietiesList, disableSelectAllSocieties, multipleSocieties]);

	useEffect(() => {
		if (multipleDevices) {
			setFinalSelectedDevices(allDevicesList);
			const societyIds = allDevicesList.map((item) => item.society_id);
			handleChangeDevice(
				allDevicesList.map((item) => item.id),
				uniq(societyIds)
			);
		} else {
			if (disableSelectAllDevices && allDevicesList.length > 0) {
				setFinalSelectedDevices([allDevicesList[0]]);
				const societyIds = allDevicesList.map((item) => item.society_id);
				handleChangeDevice([allDevicesList[0].id], uniq(societyIds));
			} else {
				setFinalSelectedDevices([]);
				handleChangeDevice([], []);
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [allDevicesList, disableSelectAllDevices, multipleDevices]);

	return (
		<Box sx={{ display: "flex", alignItems: "center", gap: "1rem" }}>
			{authState.user.role === USER_ROLES_E.SYSTEM_INTEGRATOR ? (
				<Box
					component="div"
					className={`device-selection-button ${colorMode}`}
					ref={societyDropdownAnchorRef}
					aria-haspopup
					id="property-selection-dropdown-button"
					onClick={handleOpenSocietyDropdown}
					aria-expanded={societyDropdownOpen}
					aria-controls="property-selection-dropdown-menu"
				>
					<Box className="device-selection-button-text">
						<ApartmentIcon color="inherit" fontSize="small" />
						<Typography fontWeight={500}>{societyDropdownText}</Typography>
					</Box>

					{allSocietiesList.length > 1 ? (
						societyDropdownOpen ? (
							<ArrowDropUpIcon color="inherit" fontSize="small" />
						) : (
							<ArrowDropDownIcon color="inherit" fontSize="small" />
						)
					) : null}
				</Box>
			) : null}

			{authState.user.role === USER_ROLES_E.SYSTEM_INTEGRATOR && allSocietiesList.length > 1 ? (
				<Menu
					disableAutoFocusItem
					id="property-selection-dropdown-menu"
					anchorEl={societyDropdownAnchorRef.current}
					open={societyDropdownOpen}
					onClose={() => handleCloseSocietyDropdown()}
					MenuListProps={{ disablePadding: true, "aria-labelledby": "property-selection-dropdown-button" }}
					slotProps={{ paper: { elevation: 1 } }}
				>
					{!disableSelectAllSocieties ? (
						<MenuItem
							divider
							selected={
								!multipleSocieties &&
								(finalSelectedSocieties.length === 0 || finalSelectedSocieties.length === allSocietiesList.length)
							}
							onClick={() => handleSocietyItemClick()}
							classes={{ root: "device-selection-dropdown-menu-item", selected: "device-item-selected" }}
						>
							{multipleSocieties ? (
								<ListItemIcon>{renderCheckbox(selectedSocieties.length, allSocietiesList.length)}</ListItemIcon>
							) : null}
							All Properties
						</MenuItem>
					) : null}

					{allSocietiesList.map((societyItem) => (
						<MenuItem
							divider
							selected={!multipleSocieties && selectedSocietyIds.includes(societyItem.id)}
							key={societyItem.id}
							onClick={() => handleSocietyItemClick(societyItem)}
							classes={{ root: "device-selection-dropdown-menu-item", selected: "device-item-selected" }}
						>
							{multipleSocieties ? (
								<ListItemIcon>
									{selectedSocietyIds.includes(societyItem.id) ? (
										<CheckBoxIcon color="primary" />
									) : (
										<CheckBoxOutlineBlankIcon color="primary" />
									)}
								</ListItemIcon>
							) : null}

							{societyItem.name}
						</MenuItem>
					))}

					{multipleSocieties ? (
						<Box className="multiple-selection-dropdown-footer">
							{societyError ? (
								<Typography variant="body2" color="var(--color-error-main)">
									{societyError}
								</Typography>
							) : null}

							<Button disableElevation color="primary" onClick={handleSocietyApplyButtonClick}>
								Apply
							</Button>
						</Box>
					) : null}
				</Menu>
			) : null}

			<Box
				component="div"
				className={`device-selection-button ${colorMode}`}
				ref={deviceDropdownAnchorRef}
				aria-haspopup="true"
				id="device-selection-dropdown-button"
				onClick={handleOpenDeviceDropdown}
				aria-expanded={deviceDropdownOpen}
				aria-controls="device-selection-dropdown-menu"
			>
				<Box className="device-selection-button-text">
					<DeveloperBoardIcon color="inherit" fontSize="small" />
					<Typography fontWeight={500}>{deviceDropdownText}</Typography>
				</Box>

				{allDevicesList.length > 1 ? (
					deviceDropdownOpen ? (
						<ArrowDropUpIcon color="inherit" fontSize="small" />
					) : (
						<ArrowDropDownIcon color="inherit" fontSize="small" />
					)
				) : null}
			</Box>

			{allDevicesList.length > 1 ? (
				<Menu
					disableAutoFocusItem
					id="device-selection-dropdown-menu"
					anchorEl={deviceDropdownAnchorRef.current}
					open={deviceDropdownOpen}
					onClose={() => handleCloseDeviceDropdown()}
					MenuListProps={{ disablePadding: true, "aria-labelledby": "device-selection-dropdown-button" }}
					slotProps={{ paper: { elevation: 1 } }}
				>
					{!disableSelectAllDevices ? (
						<MenuItem
							divider
							selected={
								!multipleDevices &&
								(finalSelectedDevices.length === 0 || finalSelectedDevices.length === allDevicesList.length)
							}
							onClick={() => handleDeviceItemClick()}
							classes={{ root: "device-selection-dropdown-menu-item", selected: "device-item-selected" }}
						>
							{multipleDevices ? (
								<ListItemIcon>{renderCheckbox(selectedDevices.length, allDevicesList.length)}</ListItemIcon>
							) : null}
							All Devices
						</MenuItem>
					) : null}

					{allDevicesList.map((deviceItem) => (
						<MenuItem
							divider
							selected={!multipleDevices && selectedDeviceIds.includes(deviceItem.id)}
							key={deviceItem.id}
							onClick={() => handleDeviceItemClick(deviceItem)}
							classes={{ root: "device-selection-dropdown-menu-item", selected: "device-item-selected" }}
						>
							{multipleDevices ? (
								<ListItemIcon>
									{selectedDeviceIds.includes(deviceItem.id) ? (
										<CheckBoxIcon color="primary" />
									) : (
										<CheckBoxOutlineBlankIcon color="primary" />
									)}
								</ListItemIcon>
							) : null}

							{deviceItem.device_name}
						</MenuItem>
					))}

					{multipleDevices ? (
						<Box className="multiple-selection-dropdown-footer">
							{deviceError ? (
								<Typography variant="body2" color="var(--color-error-main)">
									{deviceError}
								</Typography>
							) : null}

							<Button disableElevation color="primary" onClick={handleDeviceApplyButtonClick}>
								Apply
							</Button>
						</Box>
					) : null}
				</Menu>
			) : null}
		</Box>
	);
};

export default DeviceSelection;
